import React, { useState, useCallback } from 'react'
import SearchComponent from '../components/SearchComponent';
import { Dropdown } from 'react-bootstrap';
import AdminLayout from '../../../layouts/AdminLayout';
import CreateInventoryCategoryDialog from '../components/CreateInventoryCategoryDialog';
import useIndexInventoryCategory from '../../../services/inventory-category/hooks/useIndexInventoryCategory';
import { useDeleteInventoryCategory } from '../../../services/inventory-category/hooks/useDeleteInventoryCategory';
import { InventoryCategoryData } from '../../../services/inventory-category/response/IndexInventoryCategoryResponse';
import { toast } from 'sonner';
import { useQueryClient } from '@tanstack/react-query';
import EditInventoryCategoryDialog from '../components/EditInventoryCategoryDialog';
import PaginationComponent from '../../../shared/components/pagination/PaginationComponent';
import RequireAuth from '../../../auth/middleware/RequireAuth';
import ReadOnlyGuard from '../../../shared/components/ReadOnlyGuard';
import PropertyInactiveBanner from '../../../shared/components/PropertyInactiveBanner';

const InventoryCategoryPage: React.FC = () => {
    const [page, setPage] = useState<number>(1);
    const [searchQuery, setSearchQuery] = useState<string>('');

    const [show, setShow] = useState<boolean>(false);

    const [editState, setEditState] = useState<boolean>(false);
    const [inventoryCategoryIdWillEdit, setInventoryCategoryIdWillEdit] = useState<string>('');

    const { data: inventoryCategoriesData, isSuccess, isError, isLoading } = useIndexInventoryCategory({
        search: searchQuery,
        page: page,
        limit: 10,
    });

    const { mutate, isPending } = useDeleteInventoryCategory();

    const queryClient = useQueryClient();

    const onRemove = useCallback((e: React.MouseEvent, data: InventoryCategoryData) => {
        e.preventDefault();
        e.stopPropagation();

        if (isPending) return;

        toast.loading('Removing inventory category...', { id: 'remove-inventory-category' });

        mutate({ id: data.id }, {
            onSuccess: async () => {
                await queryClient.refetchQueries({
                    predicate: (query) => query.queryKey.some((key) => ['inventory-categories'].includes(key as string))
                });

                toast.success('Inventory category removed successfully', { id: 'remove-inventory-category' });
            },
            onError: (mutationError) => {
                toast.error(mutationError?.message || 'Failed to remove inventory category', { id: 'remove-inventory-category' });
            }
        });
    }, [mutate, isPending, queryClient]);

    const handleOpenUpdate = useCallback((e: React.MouseEvent, inventoryCategoryId: string) => {
        e.preventDefault();
        e.stopPropagation();
        setInventoryCategoryIdWillEdit(inventoryCategoryId);
        setEditState(true);
    }, []);

    const handleCloseUpdate = () => {
        setInventoryCategoryIdWillEdit('');
        setEditState(false);
    }

    return (
        <AdminLayout>
            <div className="container-fluid mt-5">
                <div className="table-container min-vh-100">
                    <h3 className="mb-3">Inventory Categories</h3>
                    <PropertyInactiveBanner />
                    <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center mb-3 gap-3">
                        <SearchComponent queryKey={searchQuery} onSearch={setSearchQuery} />
                        <RequireAuth requiredRoles={['dev']} requiredPermissions={['inventory_category_create']} asComponent>
                            <ReadOnlyGuard>
                                <button className="btn btn-primary" onClick={() => setShow(true)}>
                                    Create
                                </button>
                            </ReadOnlyGuard>
                        </RequireAuth>
                    </div>
                    <div className="inventory-category-table-wrapper">
                        <table className="table table-striped d-none d-md-table">
                            <thead>
                                <tr>
                                    <th>Name</th>
                                    <th className='text-end'>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {isError && <tr><td colSpan={2}>Failed to load data.</td></tr>}
                                {isLoading && <tr><td colSpan={2}>Loading...</td></tr>}
                                {isSuccess && !isLoading && inventoryCategoriesData.data.length > 0 && inventoryCategoriesData.data.map((inventoryCategory) => (
                                    <tr key={inventoryCategory.id}>
                                        <td>{inventoryCategory.name}</td>
                                        <td className="text-end">
                                            <Dropdown>
                                                <Dropdown.Toggle variant="default" id={`dropdownMenuButton-${inventoryCategory.id}`}>
                                                    Actions
                                                </Dropdown.Toggle>
                                                <Dropdown.Menu>
                                                    <RequireAuth requiredRoles={['dev']} requiredPermissions={['inventory_category_update']} asComponent>
                                                        <ReadOnlyGuard mode="hide">
                                                            <Dropdown.Item onClick={(e) => handleOpenUpdate(e, inventoryCategory.id)}>Edit</Dropdown.Item>
                                                        </ReadOnlyGuard>
                                                    </RequireAuth>
                                                    <RequireAuth requiredRoles={['dev']} requiredPermissions={['inventory_category_delete']} asComponent>
                                                        <ReadOnlyGuard mode="hide">
                                                            <Dropdown.Item onClick={(e) => onRemove(e, inventoryCategory)} className='text-danger' disabled={isPending}>
                                                                Remove
                                                            </Dropdown.Item>
                                                        </ReadOnlyGuard>
                                                    </RequireAuth>
                                                </Dropdown.Menu>
                                            </Dropdown>
                                        </td>
                                    </tr>
                                ))}
                                {isSuccess && !isLoading && inventoryCategoriesData.data.length === 0 && (
                                    <tr><td colSpan={2} className="text-center">No data available.</td></tr>
                                )}
                            </tbody>
                        </table>

                        {/* Tampilan Card untuk Layar Mobile */}
                        <div className="d-md-none">
                            {isError && <div className="text-center my-4">Failed to load data.</div>}
                            {isLoading && <div className="text-center my-4">Loading...</div>}
                            {isSuccess && !isLoading && inventoryCategoriesData.data.length > 0 && inventoryCategoriesData.data.map((inventoryCategory) => (
                                <div key={inventoryCategory.id} className="card mb-3">
                                    <div className="card-body">
                                        <h5 className="card-title">{inventoryCategory.name}</h5>
                                        <div className="d-flex justify-content-end mt-3">
                                            <Dropdown>
                                                <Dropdown.Toggle variant="default" id={`dropdownMenuButton-mobile-${inventoryCategory.id}`}>Actions</Dropdown.Toggle>
                                                <Dropdown.Menu>
                                                    <RequireAuth requiredRoles={['dev']} requiredPermissions={['inventory_category_update']} asComponent>
                                                        <ReadOnlyGuard mode="hide">
                                                            <Dropdown.Item onClick={(e) => handleOpenUpdate(e, inventoryCategory.id)}>Edit</Dropdown.Item>
                                                        </ReadOnlyGuard>
                                                    </RequireAuth>
                                                    <RequireAuth requiredRoles={['dev']} requiredPermissions={['inventory_category_delete']} asComponent>
                                                        <ReadOnlyGuard mode="hide">
                                                            <Dropdown.Item onClick={(e) => onRemove(e, inventoryCategory)} className='text-danger' disabled={isPending}>Remove</Dropdown.Item>
                                                        </ReadOnlyGuard>
                                                    </RequireAuth>
                                                </Dropdown.Menu>
                                            </Dropdown>
                                        </div>
                                    </div>
                                </div>
                            ))}
                            {isSuccess && !isLoading && inventoryCategoriesData.data.length === 0 && (
                                <div className="text-center my-4">No data available.</div>
                            )}
                        </div>
                    </div>
                    {isSuccess && !isLoading && inventoryCategoriesData.data.length !== 0 && inventoryCategoriesData.pagination && (
                        <PaginationComponent
                            totalPages={inventoryCategoriesData.pagination.last_page}
                            currentPage={inventoryCategoriesData.pagination.current_page}
                            onPageChange={(page) => setPage(page)}
                        />
                    )}
                </div>
            </div>
            <RequireAuth requiredRoles={['dev']} requiredPermissions={['inventory_category_create']} asComponent>
                <ReadOnlyGuard mode="hide">
                    <CreateInventoryCategoryDialog show={show} onClose={() => setShow(false)} />
                </ReadOnlyGuard>
            </RequireAuth>
            {editState && inventoryCategoryIdWillEdit && <RequireAuth requiredRoles={['dev']} requiredPermissions={['inventory_category_update']} asComponent><ReadOnlyGuard mode="hide"><EditInventoryCategoryDialog show={editState} onClose={handleCloseUpdate} inventoryCategoryId={inventoryCategoryIdWillEdit} /></ReadOnlyGuard></RequireAuth>}
        </AdminLayout>
    )
}

export default InventoryCategoryPage
